<template>
  <div v-if="showModal" :class="$utilModalStyle.modalOverlay" @click="closeModal">
    <div :class="[$utilModalStyle.modalContent, $style.modalContentLarge]" @click.stop>
      <div :class="$style.modalHeader">
        <h3 :class="$utilModalStyle.modalTitle">{{ translate("global-view-title") }}</h3>
        <button :class="$style.closeButton" @click="closeModal">
          <img :src="closeIcon" :alt="translate('close')" :class="$style.closeIcon" />
        </button>
      </div>
      <div :class="$style.modalBody">
        <div v-if="loading" :class="$style.loading">{{ translate("global-view-loading") }}</div>
        <div v-else-if="error" :class="$style.error">{{ error }}</div>
        <div v-else-if="users.length === 0" :class="$style.empty">{{ translate("global-view-empty") }}</div>
        <template v-else>
          <div :class="$style.roleTabs">
            <button
              type="button"
              v-for="tab in roleTabs"
              :key="tab.key"
              :class="[
                $style.roleTab,
                roleColorClass(tab.key),
                selectedRoleTab === tab.key && roleColorActiveClass(tab.key),
              ]"
              @click="selectedRoleTab = tab.key"
            >
              {{ translate(tab.labelKey) }}
            </button>
          </div>
          <div v-if="filteredUsers.length === 0" :class="$style.empty">
            {{ translate("global-view-empty-role", { role: selectedRoleTabLabel }) }}
          </div>
          <div v-else :class="$style.usersList">
            <div :class="$style.userRow" v-for="user in filteredUsers" :key="user.id">
              <div :class="$style.userInfo">
                <div :class="$style.userName">{{ user.firstName }} {{ user.lastName }}</div>
                <div :class="$style.userEmail">{{ user.email }}</div>
              </div>
              <div :class="$style.roleControl" @click.stop>
                <template v-if="canChangeUserRole(user) && editingRoleUserId === user.id">
                  <div :class="$style.roleEditActions">
                    <button
                      type="button"
                      :class="[$style.roleEditButton, $style.roleApplyButton]"
                      :disabled="roleChangeLoadingId === user.id || pendingRole === roleKey(user)"
                      @click="applyUserRole(user)"
                    >
                      {{ translate("apply") }}
                    </button>
                    <button
                      type="button"
                      :class="[$style.roleEditButton, $style.roleRevertButton]"
                      :disabled="roleChangeLoadingId === user.id"
                      @click="revertRoleSelector"
                    >
                      {{ translate("revert") }}
                    </button>
                  </div>
                  <select
                    :class="[$style.roleSelect, roleColorClass(pendingRole || roleKey(user))]"
                    v-model="pendingRole"
                    :disabled="roleChangeLoadingId === user.id"
                  >
                    <option v-for="role in roleOptions" :key="role.value" :value="role.value">
                      {{ role.label }}
                    </option>
                  </select>
                </template>
                <button
                  v-else
                  type="button"
                  :class="[
                    $style.userRoleLabel,
                    roleColorClass(roleKey(user)),
                    canChangeUserRole(user) && $style.userRoleLabelEditable,
                  ]"
                  :disabled="!canChangeUserRole(user) || roleChangeLoadingId === user.id"
                  @click="openRoleSelector(user)"
                >
                  {{ roleChangeLoadingId === user.id ? translate("saving") : roleLabel(user) }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div :class="$style.modalFooter">
        <button :class="$style.closeBtn" @click="closeModal">{{ translate("close") }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import utilModalStyles from "@/css/UtilModal.module.css";
import styles from "@/css/GlobalViewModal.module.css";
import CloseIcon from "@/assets/icons/close-red.svg";
import gameService from "@/services/game.service";
import { toast } from "vue3-toastify";
import { USER_ROLES } from "@/utils/permissions";

export default {
  name: "GlobalViewModal",
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    closeModal: {
      type: Function,
      required: true,
    },
    viewerRole: {
      type: String,
      default: USER_ROLES.ADMIN,
    },
  },
  data() {
    return {
      users: [],
      availableRoles: [USER_ROLES.STUDENT, USER_ROLES.TEACHER, USER_ROLES.RESEARCHER],
      selectedRoleTab: "student",
      editingRoleUserId: null,
      pendingRole: null,
      roleChangeLoadingId: null,
      loading: false,
      error: null,
      closeIcon: CloseIcon,
    };
  },
  computed: {
    $utilModalStyle() {
      return utilModalStyles;
    },
    $style() {
      return styles;
    },
    normalizedUsers() {
      return this.users.map((user) => {
        const isTeacher = user.isTeacher === true || user.isTeacher === 1 || user.isTeacher === "1";
        const isAdmin = user.isAdmin === true || user.isAdmin === 1 || user.isAdmin === "1";
        const isResearcher = user.isResearcher === true || user.isResearcher === 1 || user.isResearcher === "1";
        const hasExplicitStudentFlag = user.isStudent === true || user.isStudent === 1 || user.isStudent === "1";
        const isStudent = hasExplicitStudentFlag || (!isTeacher && !isAdmin && !isResearcher);

        return {
          ...user,
          isStudent,
          isTeacher,
          isAdmin,
          isResearcher,
        };
      });
    },
    roleTabs() {
      const tabs = [
        { key: "student", labelKey: "global-view-role-student" },
        { key: "teacher", labelKey: "global-view-role-teacher" },
      ];
      if (this.availableRoles.includes(USER_ROLES.RESEARCHER) || this.normalizedUsers.some((user) => user.isResearcher)) {
        tabs.push({ key: "researcher", labelKey: "global-view-role-researcher" });
      }
      if (this.viewerRole === USER_ROLES.ADMIN) {
        tabs.push({ key: "admin", labelKey: "global-view-role-admin" });
      }
      return tabs;
    },
    canManageRoles() {
      return this.viewerRole === USER_ROLES.ADMIN;
    },
    roleOptions() {
      return this.availableRoles
        .filter((role) => role !== USER_ROLES.ADMIN)
        .map((role) => ({
          value: role,
          label: this.translate(this.roleTranslationKey(role)),
        }));
    },
    selectedRoleTabLabel() {
      const selectedTab = this.roleTabs.find((tab) => tab.key === this.selectedRoleTab);
      return selectedTab ? this.translate(selectedTab.labelKey) : this.selectedRoleTab;
    },
    filteredUsers() {
      if (this.selectedRoleTab === "student") {
        return this.normalizedUsers.filter((user) => user.isStudent);
      }
      if (this.selectedRoleTab === "teacher") {
        return this.normalizedUsers.filter((user) => user.isTeacher);
      }
      if (this.selectedRoleTab === "researcher") {
        return this.normalizedUsers.filter((user) => user.isResearcher);
      }
      if (this.selectedRoleTab === "admin") {
        return this.normalizedUsers.filter((user) => user.isAdmin);
      }
      return this.normalizedUsers;
    },
  },
  watch: {
    showModal(newVal) {
      if (newVal) {
        this.fetchUsers();
      } else {
        this.users = [];
        this.availableRoles = [USER_ROLES.STUDENT, USER_ROLES.TEACHER, USER_ROLES.RESEARCHER];
        this.selectedRoleTab = "student";
        this.editingRoleUserId = null;
        this.pendingRole = null;
        this.roleChangeLoadingId = null;
        this.error = null;
      }
    },
  },
  methods: {
    translate(key, params = {}) {
      return this.$t(key, params);
    },
    roleTranslationKey(role) {
      const roleKeyMap = {
        [USER_ROLES.STUDENT]: "global-view-role-student",
        [USER_ROLES.TEACHER]: "global-view-role-teacher",
        [USER_ROLES.RESEARCHER]: "global-view-role-researcher",
        [USER_ROLES.ADMIN]: "global-view-role-admin",
      };
      return roleKeyMap[role] || role;
    },
    roleKey(user) {
      if (user?.isAdmin) return USER_ROLES.ADMIN;
      if (user?.isResearcher) return USER_ROLES.RESEARCHER;
      if (user?.isTeacher) return USER_ROLES.TEACHER;
      return USER_ROLES.STUDENT;
    },
    roleLabel(user) {
      return this.translate(this.roleTranslationKey(this.roleKey(user)));
    },
    roleColorClass(role) {
      const classByRole = {
        [USER_ROLES.STUDENT]: this.$style.studentRole,
        [USER_ROLES.TEACHER]: this.$style.teacherRole,
        [USER_ROLES.RESEARCHER]: this.$style.researcherRole,
        [USER_ROLES.ADMIN]: this.$style.adminRole,
      };
      return classByRole[role] || this.$style.studentRole;
    },
    roleColorActiveClass(role) {
      const classByRole = {
        [USER_ROLES.STUDENT]: this.$style.studentRoleActive,
        [USER_ROLES.TEACHER]: this.$style.teacherRoleActive,
        [USER_ROLES.RESEARCHER]: this.$style.researcherRoleActive,
        [USER_ROLES.ADMIN]: this.$style.adminRoleActive,
      };
      return classByRole[role] || this.$style.studentRoleActive;
    },
    canChangeUserRole(user) {
      return this.canManageRoles && user && !user.isAdmin;
    },
    openRoleSelector(user) {
      if (!this.canChangeUserRole(user)) return;
      this.editingRoleUserId = user.id;
      this.pendingRole = this.roleKey(user);
    },
    revertRoleSelector() {
      this.editingRoleUserId = null;
      this.pendingRole = null;
    },
    async applyUserRole(user) {
      const role = this.pendingRole;
      if (!this.canChangeUserRole(user) || role === this.roleKey(user)) {
        this.revertRoleSelector();
        return;
      }
      this.roleChangeLoadingId = user.id;
      try {
        const res = await gameService.updateUserRole(user.id, role);
        if (!res?.success || !res.user) {
          throw new Error(res?.message || this.translate("global-view-update-role-failed"));
        }
        this.users = this.users.map((currentUser) => (
          currentUser.id === user.id ? res.user : currentUser
        ));
        toast(this.translate("global-view-role-updated"), { type: "success" });
      } catch (err) {
        const message = err?.message || this.translate("global-view-update-role-failed");
        toast(message, { type: "error" });
      } finally {
        this.editingRoleUserId = null;
        this.pendingRole = null;
        this.roleChangeLoadingId = null;
      }
    },
    async fetchUsers() {
      this.loading = true;
      this.error = null;
      try {
        const res = await gameService.getAllUsers();
        if (res.status === 200 && res.data.success) {
          this.users = res.data.users || [];
          if (Array.isArray(res.data.availableRoles) && res.data.availableRoles.length > 0) {
            this.availableRoles = res.data.availableRoles;
          }
        } else {
          this.error = res.data?.message || this.translate("global-view-fetch-users-failed");
          toast(this.error, { type: "error" });
        }
      } catch (err) {
        console.error("Error fetching users:", err);
        this.error = this.translate("global-view-fetch-users-error");
        toast(this.error, { type: "error" });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style module>
</style>
